import { Box, List, ListItem, styled, useTheme } from "@outschool/backpack";
import { useLinkComponent } from "@outschool/ui-utils";
import React, { useMemo } from "react";

import {
  buildCategoryTrackingProperties,
  CATEGORY_NAVBAR_COMPONENT,
} from "./categoryNavbarTracking";
import { getCategoryUrl } from "./categoryNavbarUtils";
import { CategoryNavLink } from "./navbarCategories";

export function DesktopDropdown({
  category: parentCategory,
  closeMenu,
  useSearchLinks = false,
}: {
  category: CategoryNavLink;
  closeMenu: () => void;
  useSearchLinks?: boolean;
}) {
  const Link = useLinkComponent();
  const theme = useTheme();

  const CategoryLink = styled(Link)<{ disabled?: boolean }>`
    color: ${theme.colors.gray900};
    font-weight: 500;
    font-size: 1em;
    text-decoration: none;
    pointer-events: ${props => (props.disabled ? "none" : "auto")};
    cursor: ${props => (props.disabled ? "default" : "pointer")};
    &:hover,
    &:focus-within,
    &:focus,
    &:focus-visible {
      color: ${props =>
        props.disabled ? theme.colors.gray900 : theme.palette.primary.main};
      text-decoration: ${props => (props.disabled ? "none" : "underline")};
    }
  `;

  const SubcategoryLink = styled(Link)`
    color: ${theme.colors.gray900};
    font-size: 0.875em;
    text-decoration: none;
    &:hover,
    &:focus-within,
    &:focus,
    &:focus-visible {
      color: ${theme.palette.primary.main};
      text-decoration: underline;
    }
  `;

  const columnCount = useMemo(
    () => countColumns(parentCategory.getSubcategories?.() ?? []),
    [parentCategory],
  );

  const subcategoriesMemo = useMemo(() => {
    return (
      <>
        {parentCategory.getSubcategories?.().map((category, index) => (
          <List
            key={`${parentCategory.slug}:${category.slug}:${index}`}
            sx={{
              paddingTop: 0,
              paddingBottom: 18,
              WebkitColumnBreakInside: "avoid",
              pageBreakInside: "avoid",
              breakInside: "avoid",
              [theme.breakpoints.down("lg")]: {
                WebkitColumnBreakInside: "auto",
                pageBreakInside: "auto",
                breakInside: "auto",
              },
            }}
          >
            <CategoryLink
              to={getCategoryUrl(category, useSearchLinks)}
              trackingName="category-navbar-desktop-popup-item"
              trackingUniqueId={`${parentCategory.slug}/${category.slug}`}
              sharedProperties={buildCategoryTrackingProperties(category, {
                component: CATEGORY_NAVBAR_COMPONENT.HORIZONTAL_NAVBAR,
                ancestors: [parentCategory],
              })}
              trackViews={false}
              onClick={closeMenu}
              disabled={category.can_link === false}
              tabIndex={category.can_link === false ? -1 : undefined}
            >
              <ListItem dense disableGutters>
                {category.nameTitleCased}
              </ListItem>
            </CategoryLink>
            {category.getSubcategories?.().map(subcategory =>
              subcategory.getSubcategories &&
              subcategory.getSubcategories?.().length > 0 ? (
                <React.Fragment key={subcategory.slug}>
                  <ListItem
                    dense
                    disableGutters
                    sx={{
                      fontWeight: 500,
                      fontSize: "1.6rem",
                      marginTop: ".4rem",
                    }}
                  >
                    {subcategory.nameTitleCased}
                  </ListItem>
                  {subcategory.getSubcategories?.().map(nestedSub => (
                    <SubcategoryLink
                      key={nestedSub.slug}
                      to={getCategoryUrl(nestedSub, useSearchLinks)}
                      trackingName="category-navbar-desktop-popup-item"
                      trackingUniqueId={`${subcategory.slug}/${nestedSub.slug}`}
                      sharedProperties={buildCategoryTrackingProperties(
                        nestedSub,
                        {
                          component:
                            CATEGORY_NAVBAR_COMPONENT.HORIZONTAL_NAVBAR,
                          ancestors: [subcategory, category, parentCategory],
                        },
                      )}
                      trackViews={false}
                      onClick={closeMenu}
                    >
                      <ListItem dense disableGutters>
                        {nestedSub.nameTitleCased}
                      </ListItem>
                    </SubcategoryLink>
                  ))}
                </React.Fragment>
              ) : (
                <SubcategoryLink
                  key={subcategory.slug}
                  to={getCategoryUrl(subcategory, useSearchLinks)}
                  trackingName="category-navbar-desktop-popup-item"
                  trackingUniqueId={`${category.slug}/${subcategory.slug}`}
                  sharedProperties={buildCategoryTrackingProperties(
                    subcategory,
                    {
                      component: CATEGORY_NAVBAR_COMPONENT.HORIZONTAL_NAVBAR,
                      ancestors: [category, parentCategory],
                    },
                  )}
                  trackViews={false}
                  onClick={closeMenu}
                >
                  <ListItem dense disableGutters>
                    {subcategory.nameTitleCased}
                  </ListItem>
                </SubcategoryLink>
              ),
            )}
          </List>
        ))}
      </>
    );
  }, [
    parentCategory,
    CategoryLink,
    SubcategoryLink,
    closeMenu,
    theme.breakpoints,
    useSearchLinks,
  ]);

  if (
    !parentCategory.getSubcategories ||
    parentCategory.getSubcategories?.().length === 0
  ) {
    return null;
  }

  return (
    <Box
      sx={{
        columnCount: columnCount,
        columnWidth: 170,
        columnGap: "0.8em",
        padding: "1em",
      }}
    >
      {subcategoriesMemo}
    </Box>
  );
}

function countColumns(categories: CategoryNavLink[]): number {
  const heights: number[] = categories.map(
    cat => (cat.getSubcategories?.().length ?? 0) + 1,
  );

  const maxHeight = Math.max(...heights); // Find the height of the tallest category

  let columnCount = 0;
  let i = 0; // Left pointer

  // Loop through the categories using two pointers
  while (i < categories.length) {
    let currentHeight = heights[i];
    let j = i + 1;
    // Combine shorter categories into one column if they fit within max height
    while (j < categories.length && currentHeight + heights[j] <= maxHeight) {
      currentHeight += heights[j];
      j++;
    }
    columnCount++;
    i = j;
  }

  return columnCount;
}
